import { observable } from 'mobx';
import { DataSyncExecution } from './DataSyncExecution';
import { CGLanguage } from './ContentGraph';
import { FieldMappingConverter } from '../utils/fieldMappingConverter';
import { FieldDefinition } from './FieldDefinition';
import { BaseContractFieldType, DestinationSchemaFieldType } from './Schema';

export enum OptimizelyProduct {
  HUB = 'HUB',
  CGR = 'CGR',
  CMS = 'CMS',
  CMP = 'CMP',
  EXP = 'EXP',
  HUB_APP = 'HUB_APP',
}

export enum MappingMode {
  EDITABLE = 'Editable',
  FIXED = 'Fixed',
}

export enum Target {
  SOURCE = 'Source',
  DESTINATION = 'Destination',
}

export interface ProductInstance {
  id: string;
  name: string;
}

export interface CGProductInstance {
  cg_instance: ProductInstance;
  associated_cms_instance: ProductInstance;
}

export interface ProductInstances {
  product: OptimizelyProduct;
  instances: ProductInstance[];
}

export interface ObjectDefinition {
  object_name: string;
  object_display_name: string;
  object_icon_url: string;
  fields: FieldDefinition[];
  disabled?: boolean;
  warning?: string;
}

export interface CGObjectDefinition extends ObjectDefinition {
  base_type?: string;
  source_product: OptimizelyProduct;
  source_object_name: string;
  created_by: string;
}

export interface CGBaseTypeDefinition {
  name: string;
  display_name: string;
  fields: CGBaseTypeFieldDefinition[];
}
export interface CGBaseTypeFieldDefinition {
  name: string;
  display_name: string;
  type?: DestinationSchemaFieldType;
  primitive_type?: BaseContractFieldType;
  object_schema?: {
    fields: CGBaseTypeFieldDefinition[];
  };
}

export interface CGContentType {
  type_name: string;
  base_types: string[];
  source_product: OptimizelyProduct;
  source_object_name: string;
  created_by: string;
  fields: CGContentTypeField[];
  property_types: CGPropertyTypeField[];
}

export interface CGContentTypeField {
  name: string;
  type: string;
}

export interface CGPropertyTypeField {
  name: string;
  fields: CGContentTypeField[];
}

export interface SourceObject {
  object_name: string;
  object_display_name?: string;
  object_icon_url?: string;
  app_id?: string;
}

export interface Source {
  product: OptimizelyProduct;
  instance: ProductInstance;
  config: SourceConfig;
  object: SourceObject;
}

export interface DestinationObject {
  object_name: string;
  object_icon_url?: string;
}

export interface Destination {
  product: OptimizelyProduct;
  instance: ProductInstance;
  config: DestinationConfig;
  object: DestinationObject;
}

export interface Mappings {
  [key: string]: {
    source_field: string;
    disabled?: boolean;
  };
}

export const CG_BASE_CONTRACT_FIELDS = ['_itemMetadata', '_assetMetadata', '_imageMetadata'];

// PropertyMapping matches the proto definition exactly
export interface PropertyMapping {
  // oneof mapping_type
  source_field?: string; // maps to source_field in proto
  nested?: NestedPropertyMapping; // maps to nested in proto
}

// NestedPropertyMapping matches the proto definition exactly
export interface NestedPropertyMapping {
  root_object_field?: string; // maps to root_object_field in proto
  properties: { [key: string]: PropertyMapping }; // maps to properties in proto
}

// NestedFieldMapping matches the proto definition exactly
export interface NestedFieldMapping {
  properties: { [key: string]: PropertyMapping }; // maps to properties in proto
}

// For backward compatibility - keep the old interface names as aliases
export interface NestedMappingProperties {
  [key: string]: PropertyMapping;
}

export interface NestedMappings {
  properties: NestedMappingProperties;
}

export interface FieldMappings {
  // Legacy format - will be deprecated
  mappings?: Mappings;
  // New proto-compliant format
  nested_mappings?: NestedFieldMapping;
}

export interface DestinationConfig {
  content_graph?: ContentGraphConfig;
}

export interface SourceConfig {
  content_graph?: ContentGraphConfig;
}

export enum CGDeleteMode {
  SOFT_DELETE = 'SOFT_DELETE',
  HARD_DELETE = 'HARD_DELETE',
}

export interface CGDeleteConfig {
  mode: CGDeleteMode;
}

export interface ContentGraphConfig {
  language: string;
  status: string;
  cms_integration_enabled?: boolean;
  base_contract?: string;
  cms_instance_id?: string;
  cms_sync_status?: string;
  cms_sync_error?: string;
  delete_config?: CGDeleteConfig;
}

export const dataSyncScheduleIntervals = [
  {text: 'Every 10 minutes', value: '600s'},
  {text: 'Every 20 minutes', value: '1200s'},
  {text: 'Every 30 minutes', value: '1800s'},
  {text: 'Every 1 hour', value: '3600s'},
  {text: 'Every 2 hours', value: '7200s'},
  {text: 'Every 3 hours', value: '10800s'},
  {text: 'Every 4 hours', value: '14400s'},
  {text: 'Every 6 hours', value: '21600s'},
  {text: 'Every 12 hours', value: '43200s'},
  {text: 'Every 1 day', value: '86400s'},
  {text: 'Every 2 days', value: '172800s'}
];

export enum SyncStrategy {
  WEBHOOK,
  SCHEDULED
}

export interface HubApp {
  app_id: string;
  version: string;
  name: string;
  icon_url: string;
}

export interface CGSourceMetadata {
  languages: CGLanguage[];
}

export class DataSyncItem {

  public static createEmpty(): DataSyncItem {
    return new DataSyncItem({
      id: undefined,
      name: '',
      enabled: true,
      tracker_id: '',
      source: {
        product: '',
        instance: {
          id: '',
          name: ''
        },
        object: {
          object_name: '',
          object_display_name: '',
          object_icon_url: '',
          app_id: ''
        }
      },
      destination: {
        product: '',
        instance: {
          id: '',
          name: ''
        },
        object: {
          object_name: '',
          object_icon_url: ''
        }
      },
      field_mappings: { mappings: {}, nested_mappings: { properties: {} } }
    });
  }
  @observable public id: string;
  @observable public tracker_id?: string;
  @observable public name: string;
  @observable public interval?: string;
  @observable public source: Source;
  @observable public destination: Destination;
  @observable public enabled: boolean;
  @observable public field_mappings: FieldMappings;
  @observable public lastExecution?: DataSyncExecution;

  constructor(obj: any) {
    this.id = obj.id;
    this.tracker_id = obj.tracker_id;
    this.name = obj.name;
    this.interval = obj.interval;
    this.enabled = obj.enabled;
    this.source = obj.source;
    this.destination = obj.destination;

    // Handle different backend response scenarios
    const rawFieldMappings = obj.field_mappings || {};

    // If backend sends only nested_mappings (new format)
    if (
      rawFieldMappings.nested_mappings?.properties &&
      Object.keys(rawFieldMappings.nested_mappings?.properties).length > 0
    ) {
      this.field_mappings = {
        nested_mappings: rawFieldMappings.nested_mappings,
      };
    } else if (rawFieldMappings.mappings && !rawFieldMappings.nested_mappings) {
      this.field_mappings = FieldMappingConverter.convertToNestedMappings({
        mappings: rawFieldMappings.mappings,
      });
    } else {
      this.field_mappings = rawFieldMappings;
    }

    this.lastExecution = obj.lastExecution;
  }
}
